import React, {ReactNode, useMemo} from 'react';
import {
  Cell,
  Column,
  IdType,
  Row,
  TableState,
  useGlobalFilter,
  usePagination,
  useRowSelect,
  useSortBy,
  useTable,
} from 'react-table';

import {PredicateOf, Renderer, RendererOf} from '../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {AnalyticsMetricError} from '../../../models/api/analyticsMetricsModel';
import {MetricErrorState} from '../reportElements/charts/common/metricErrorState';
import {TableContent} from './tableContent';
import {useAddSelectionColumn} from './tableSelection/useAddSelectionColumn';
import {TableTitleBar} from './tableTitleBar/tableTitleBar';

export type GlobalFilterFunction<T extends object> = (
  rows: Row<T>[],
  columnIds: IdType<T>[],
  filterValue: any,
) => Row<T>[];

/*
 * Props.
 */

export interface TableProps<T extends object> {
  columns: Array<Column<T>>;
  data: Array<T>;
  isLoading?: boolean;
  isEmpty?: boolean;
  renderEmptyState?: RendererOf<{width?: number; height?: number}>;
  metricError?: AnalyticsMetricError;
  title?: ReactNode;
  displayTableTitleBar: boolean;
  hiddenColumnIds?: string[];
  renderCustom?: Renderer;
  renderColumnPicker?: Renderer;
  renderExportSelector?: Renderer;
  disableSelection?: boolean;
  isFirstColumnSticky?: boolean;
  disableSortBy?: boolean;
  disableGlobalFilter?: boolean;
  disableTablePagination?: boolean;
  globalFilterOption?: GlobalFilterFunction<T>;
  initialState?: Partial<TableState<T>>;
  onCellSelect?: (arg: Cell<T>) => void;
  isCellSelectable?: PredicateOf<Cell<T>>;
}
/**
 * Component.
 * This component creates the table instance and will instantiate the actual table component if if it's not empty.
 * TableContent needs to be in a separate component so the html refs needed for the sticky colomn behaviour can properly work (they musn't be conditionnaly rendered)
 */

export const Table = <T extends object>({
  columns,
  data,
  isEmpty,
  renderEmptyState,
  metricError,
  title,
  disableSelection,
  renderCustom,
  renderColumnPicker,
  renderExportSelector,
  globalFilterOption,
  initialState,
  isLoading,
  hiddenColumnIds,
  onCellSelect,
  isCellSelectable,
  isFirstColumnSticky,
  disableSortBy,
  disableGlobalFilter,
  disableTablePagination,
  displayTableTitleBar,
}: TableProps<T>) => {
  const plugins = useMemo(
    () => [
      useGlobalFilter,
      useSortBy,
      usePagination,
      useRowSelect,
      ...(disableSelection ? [] : [useAddSelectionColumn]),
    ],
    [disableSelection],
  );

  const tableInstance = useTable<T>(
    {
      columns,
      data,
      initialState,
      globalFilter: globalFilterOption,
      disableSortRemove: true,
      autoResetSortBy: false,
      disableSortBy,
      disableGlobalFilter,
    },
    ...plugins,
  );

  const renderTable = () => {
    if (metricError) {
      return <MetricErrorState error={metricError} />;
    }

    if (isEmpty && renderEmptyState) {
      return renderEmptyState({});
    }

    return (
      <TableContent
        isLoading={isLoading}
        hiddenColumnIds={hiddenColumnIds}
        onCellSelect={onCellSelect}
        isCellSelectable={isCellSelectable}
        isFirstColumnSticky={isFirstColumnSticky}
        tableInstance={tableInstance}
        disableTablePagination={disableTablePagination}
      />
    );
  };

  return (
    <>
      {displayTableTitleBar && (
        <TableTitleBar<T>
          title={title}
          renderCustom={renderCustom}
          instance={tableInstance}
          renderColumnPicker={renderColumnPicker}
          renderExportSelector={renderExportSelector}
        />
      )}
      {renderTable()}
    </>
  );
};
